import React, { Component } from 'react';
import { Form, Button, Container, Card, Spinner, Row, Col, Alert } from 'react-bootstrap';
import { withRouter } from 'react-router-dom';
import * as Cookies from "es-cookie";

import gql from "graphql-tag";
import { graphql, withApollo } from "react-apollo";
import compose from 'lodash.flowright';

// College bridge: the viewed student comes from props (route state). Different
// entry points name the mobile field differently, so check every known variant.
const resolveMobile = (student) => {
  if (!student) return null;
  return (
    student.contact_no || student.student_mobile || student.mobile ||
    student.studentMobile || student.parent_mobile || student.phone ||
    student.mobile_no || student.username || null
  );
};

// College view: the raised doubt belongs to the viewed student, so the author
// name resolves from their profile. Falls back to "Student" if no name.
const studentName = (student) => {
  const full = (student && (student.student_name || student.name)) || "";
  return full.trim() ? full.trim() : "Student";
};

const FETCH_GET_SUBJECTS = gql`
  query ($mobile: String!) {
    getSubjects(mobile: $mobile) {
      id
      subject
      studentChapters {
        id
        chapter
      }
    }
  }
`;

const CREATE_DOUBT_MUTATION = gql`
  mutation($params: DoubtInput!) {
    createDoubt(params: $params)
  }
`;

const FETCH_LECTURERS_BY_SUBJECT = gql`
  query($subject_id: Int!, $institution_id: Int!) {
    getLecturersBySubject(subject_id: $subject_id, institution_id: $institution_id) {
      id
      employee_name
    }
  }
`;

const FILE_UPLOAD_MUTATION = gql`
  mutation($params: FileUploadInput!) {
    fileUpload(params: $params) {
      file_url
      uuid
    }
  }
`;

class CreateDoubtForm extends Component {
    constructor(props) {
        super(props);
        this.state = {
            subject: '',
            chapter: '',
            assignedFacultyId: '',
            lecturersList: [],
            title: '',
            description: '',
            file: null,
            submitting: false,
            validated: false,
            errorMsg: '',
            successMsg: '',
            fileError: '',
            filePreviewUrl: ''
        };
    }

    componentWillUnmount() {
        if (this.state.filePreviewUrl) {
            URL.revokeObjectURL(this.state.filePreviewUrl);
        }
    }

    getBase64 = (file) => {
        return new Promise((resolve, reject) => {
            const reader = new FileReader();
            reader.readAsDataURL(file);
            reader.onload = () => resolve(reader.result);
            reader.onerror = error => reject(error);
        });
    }

    handleChange = (e) => {
        this.setState({ [e.target.name]: e.target.value });
        if (e.target.name === 'subject') {
            this.setState({ chapter: '', assignedFacultyId: '', lecturersList: [] });
            this.fetchLecturers(e.target.value);
        }
    }

    fetchLecturers = async (subjectId) => {
        if (!subjectId) return;
        try {
            const res = await this.props.client.query({
                query: FETCH_LECTURERS_BY_SUBJECT,
                variables: {
                    subject_id: parseInt(subjectId),
                    institution_id: parseInt(Cookies.get("institution_id") || Cookies.get("institutionid") || "0")
                },
                fetchPolicy: "network-only"
            });
            if (res.data && res.data.getLecturersBySubject) {
                this.setState({ lecturersList: res.data.getLecturersBySubject });
            }
        } catch (error) {
            console.error("Error fetching lecturers:", error);
        }
    }

    handleFileChange = (e) => {
        const file = e.target.files[0];
        if (!file) {
            this.handleRemoveFile();
            return;
        }

        // Validate strictly image-only
        if (!file.type.startsWith('image/')) {
            this.setState({
                file: null,
                fileError: 'Please upload image files only (PNG, JPG, etc.)',
                filePreviewUrl: ''
            });
            e.target.value = ''; // Reset file input
            return;
        }

        // Validate size (5MB limit)
        if (file.size > 5 * 1024 * 1024) {
            this.setState({
                file: null,
                fileError: 'Image size must be less than 5MB',
                filePreviewUrl: ''
            });
            e.target.value = ''; // Reset file input
            return;
        }

        this.setState({
            file: file,
            fileError: '',
            filePreviewUrl: URL.createObjectURL(file)
        });
    }

    handleRemoveFile = () => {
        if (this.state.filePreviewUrl) {
            URL.revokeObjectURL(this.state.filePreviewUrl);
        }
        this.setState({ file: null, filePreviewUrl: '', fileError: '' });
        if (this.fileInputRef) {
            this.fileInputRef.value = '';
        }
    }

    handleClear = () => {
        this.handleRemoveFile();
        this.setState({
            subject: '',
            chapter: '',
            assignedFacultyId: '',
            lecturersList: [],
            title: '',
            description: '',
            submitting: false,
            validated: false,
            errorMsg: '',
            successMsg: ''
        });
    }

    handleSubmit = async (e) => {
        e.preventDefault();
        const form = e.currentTarget;
        if (form.checkValidity() === false) {
            e.stopPropagation();
            this.setState({ validated: true });
            return;
        }

        // Validation for Description minimum length
        if (this.state.description.trim().length < 10) {
            this.setState({ errorMsg: 'Description must be at least 10 characters long.' });
            return;
        }

        this.setState({ submitting: true, errorMsg: '', successMsg: '' });

        try {
            let image_path = "";

            if (this.state.file) {
                const base64File = await this.getBase64(this.state.file);
                const uploadRes = await this.props.fileUpload({
                    variables: {
                        params: {
                            category: "materials", // using generic materials folder
                            client_id: 1, // defaulting to 1 or pull from cookies
                            file: [
                                {
                                    img: base64File,
                                    filename: this.state.file.name
                                }
                            ]
                        }
                    }
                });

                if (uploadRes && uploadRes.data && uploadRes.data.fileUpload && uploadRes.data.fileUpload.length > 0) {
                    image_path = uploadRes.data.fileUpload[0].file_url;
                }
            }

            await this.props.createDoubt({
                variables: {
                    params: {
                        title: this.state.title,
                        description: this.state.description,
                        subject: parseInt(this.state.subject),
                        chapter: this.state.chapter ? parseInt(this.state.chapter) : null,
                        mobile: resolveMobile(this.props.student) || "",
                        name: studentName(this.props.student),
                        image_path: image_path,
                        institution_id: parseInt(Cookies.get("institution_id") || Cookies.get("institutionid") || "0"),
                        assigned_faculty_id: this.state.assignedFacultyId ? parseInt(this.state.assignedFacultyId) : null,
                        estatus: 1
                    }
                }
            });

            this.setState({ successMsg: "Doubt submitted successfully!" });

            setTimeout(() => {
                this.handleClear();
                if (this.props.onDoubtCreated) {
                    this.props.onDoubtCreated();
                } else {
                    this.props.history.push('/student/doubts');
                }
            }, 1200);

        } catch (error) {
            console.error("Error submitting doubt:", error);
            this.setState({ errorMsg: "Error submitting doubt: " + error.message });
        } finally {
            this.setState({ submitting: false });
        }
    }

    render() {
        const subjects = this.props.getSubjects && this.props.getSubjects.getSubjects ? this.props.getSubjects.getSubjects : [];

        let filteredChapters = [];
        if (this.state.subject) {
            const selectedSub = subjects.find(s => s.id.toString() === this.state.subject.toString());
            if (selectedSub && selectedSub.studentChapters) {
                filteredChapters = selectedSub.studentChapters;
            }
        }

        return (
            <div className="create-doubt-form-wrapper">
                <style>{`
                    .custom-file-upload-area {
                        border: 2px dashed #cbd5e1;
                        background-color: #f8fafc;
                        border-radius: 8px;
                        transition: all 0.2s ease-in-out;
                        cursor: pointer;
                    }
                    .custom-file-upload-area:hover {
                        border-color: #36a1f5;
                        background-color: #eff6ff;
                    }
                    .font-weight-medium {
                        font-weight: 600;
                        color: #475569;
                        font-size: 0.88rem;
                    }
                    .form-control {
                        border-radius: 8px;
                        border: 1px solid #cbd5e1;
                        font-size: 0.9rem;
                        padding: 10px 12px;
                    }
                    .form-control:focus {
                        border-color: #36a1f5;
                        box-shadow: 0 0 0 3px rgba(54, 161, 245, 0.15);
                    }
                `}</style>

                {this.state.successMsg && (
                    <Alert variant="success" className="mb-4 small font-weight-bold">
                        {this.state.successMsg}
                    </Alert>
                )}
                {this.state.errorMsg && (
                    <Alert variant="danger" className="mb-4 small font-weight-bold" onClose={() => this.setState({ errorMsg: '' })} dismissible>
                        {this.state.errorMsg}
                    </Alert>
                )}

                <Form noValidate validated={this.state.validated} onSubmit={this.handleSubmit}>
                    <Form.Group controlId="subjectSelect" className="mb-3">
                        <Form.Label className="font-weight-medium">
                            Subject <span className="text-danger">*</span>
                        </Form.Label>
                        <Form.Control
                            as="select"
                            name="subject"
                            value={this.state.subject}
                            onChange={this.handleChange}
                            required
                            disabled={this.state.submitting}
                        >
                            <option value="">Select Subject</option>
                            {subjects && subjects.map(sub => (
                                <option key={sub.id} value={sub.id}>{sub.subject}</option>
                            ))}
                        </Form.Control>
                        <Form.Control.Feedback type="invalid">
                            Please select a subject.
                        </Form.Control.Feedback>
                    </Form.Group>

                    <Form.Group controlId="chapterSelect" className="mb-3">
                        <Form.Label className="font-weight-medium">
                            Chapter / Topic
                        </Form.Label>
                        <Form.Control
                            as="select"
                            name="chapter"
                            value={this.state.chapter}
                            onChange={this.handleChange}
                            disabled={!this.state.subject || this.state.submitting}
                        >
                            <option value="">Select Chapter</option>
                            {filteredChapters.map(ch => (
                                <option key={ch.id} value={ch.id}>{ch.chapter}</option>
                            ))}
                        </Form.Control>
                    </Form.Group>

                    <Form.Group controlId="lecturerSelect" className="mb-3">
                        <Form.Label className="font-weight-medium">
                            Select Lecturer (Optional)
                        </Form.Label>
                        <Form.Control
                            as="select"
                            name="assignedFacultyId"
                            value={this.state.assignedFacultyId}
                            onChange={this.handleChange}
                            disabled={!this.state.subject || this.state.submitting}
                        >
                            <option value="">Select Lecturer</option>
                            {this.state.lecturersList.map(lecturer => (
                                <option key={lecturer.id} value={lecturer.id}>{lecturer.employee_name}</option>
                            ))}
                        </Form.Control>
                    </Form.Group>

                    <Form.Group controlId="doubtTitle" className="mb-3">
                        <Form.Label className="font-weight-medium">
                            Title <span className="text-danger">*</span>
                        </Form.Label>
                        <Form.Control
                            type="text"
                            name="title"
                            placeholder="Enter a short title"
                            value={this.state.title}
                            onChange={this.handleChange}
                            required
                            maxLength={100}
                            disabled={this.state.submitting}
                        />
                        <div className="d-flex justify-content-between">
                            <Form.Control.Feedback type="invalid">
                                Please enter a title.
                            </Form.Control.Feedback>
                            <Form.Text className="text-muted ml-auto small">
                                {this.state.title.length} / 100 characters
                            </Form.Text>
                        </div>
                    </Form.Group>

                    <Form.Group controlId="doubtDescription" className="mb-3">
                        <Form.Label className="font-weight-medium">
                            Description <span className="text-danger">*</span>
                        </Form.Label>
                        <Form.Control
                            as="textarea"
                            rows={4}
                            name="description"
                            placeholder="Describe the doubt in detail... (minimum 10 characters)"
                            value={this.state.description}
                            onChange={this.handleChange}
                            required
                            disabled={this.state.submitting}
                        />
                        <Form.Control.Feedback type="invalid">
                            Please describe the doubt (minimum 10 characters).
                        </Form.Control.Feedback>
                    </Form.Group>

                    <Form.Group controlId="doubtFile" className="mb-4">
                        <Form.Label className="font-weight-medium">
                            Upload Reference Image (Optional)
                        </Form.Label>
                        <div className="custom-file-upload-area p-3 text-center">
                            {this.state.filePreviewUrl ? (
                                <div className="text-center w-100">
                                    <img
                                        src={this.state.filePreviewUrl}
                                        alt="Preview"
                                        style={{ maxWidth: '100%', maxHeight: '160px', objectFit: 'contain', borderRadius: '4px', border: '1px solid #ddd' }}
                                    />
                                    <div className="mt-2 text-muted small text-truncate px-3 font-weight-medium">{this.state.file ? this.state.file.name : ''}</div>
                                    <Button
                                        variant="outline-danger"
                                        size="sm"
                                        className="mt-2 py-1 px-3"
                                        onClick={this.handleRemoveFile}
                                        disabled={this.state.submitting}
                                        style={{ borderRadius: '20px', fontSize: '0.75rem' }}
                                    >
                                        Remove Image
                                    </Button>
                                </div>
                            ) : (
                                <div className="w-100 py-2">
                                    <Form.Control
                                        type="file"
                                        name="file"
                                        accept="image/*"
                                        onChange={this.handleFileChange}
                                        ref={el => this.fileInputRef = el}
                                        style={{ display: 'none' }}
                                        id="upload-image-input"
                                        disabled={this.state.submitting}
                                    />
                                    <label htmlFor="upload-image-input" className="mb-0 w-100" style={{ cursor: 'pointer' }}>
                                        <div className="text-muted mb-2" style={{ fontSize: '1.8rem' }}>📸</div>
                                        <span className="btn btn-lightblue-outline btn-sm px-3 font-weight-bold" style={{ borderRadius: '20px', fontSize: '0.8rem' }}>Choose Image</span>
                                        <div className="text-muted small mt-2" style={{ fontSize: '0.75rem' }}>PNG, JPG, or JPEG supported (Max size: 5MB)</div>
                                    </label>
                                </div>
                            )}
                            {this.state.fileError && (
                                <div className="text-danger small mt-2 font-weight-bold">{this.state.fileError}</div>
                            )}
                        </div>
                    </Form.Group>

                    <div className="d-flex justify-content-end align-items-center mt-4">
                        <Button
                            variant="outline-secondary"
                            className="btn-sm px-4 py-2 mr-3 font-weight-bold"
                            onClick={this.props.onCancel || this.handleClear}
                            disabled={this.state.submitting}
                            style={{ borderRadius: '20px' }}
                        >
                            Cancel
                        </Button>
                        <Button
                            className="btn-lightblue btn-sm px-4 py-2 font-weight-bold text-white shadow-sm"
                            type="submit"
                            disabled={this.state.submitting || !!this.state.fileError}
                            style={{ minWidth: '130px', borderRadius: '20px' }}
                        >
                            {this.state.submitting ? (
                                <>
                                    <Spinner animation="border" size="sm" className="mr-2" />
                                    Submitting...
                                </>
                            ) : 'Submit Doubt'}
                        </Button>
                    </div>
                </Form>
            </div>
        );
    }
}

export default compose(
    withRouter,
    withApollo,
    graphql(FETCH_GET_SUBJECTS, {
        options: (props) => ({
            variables: { mobile: resolveMobile(props.student) || "" },
            fetchPolicy: "cache-and-network"
        }),
        name: "getSubjects"
    }),
    graphql(CREATE_DOUBT_MUTATION, {
        name: "createDoubt"
    }),
    graphql(FILE_UPLOAD_MUTATION, {
        name: "fileUpload"
    })
)(CreateDoubtForm);
